import { useCallback, useEffect, useRef, useState } from "react";
import {
  submitInboxAction,
  submitInboxFeedback,
  undoInboxAction,
  type InboxAction,
  type InboxDismissChip,
  type InboxReasonChip,
} from "../../lib/api";
import { track } from "../../lib/telemetry";

/** How long an action stays reversible (FR-6.4). */
export const UNDO_MS = 30_000;

/** A write that did not land, in the room's voice. The row stays; so does the retry. */
export const ACTION_FAILED_NOTE = "Nob is not answering. Try that again.";

/**
 * THE RELAY WENT AND THE REPLY DID NOT (F-58, ruling R-0914-2).
 *
 * `reply_sent: false` is not a failed action and must not read as one: the door
 * is open, the decision is recorded and the note is in the thread — the only
 * thing that did not happen is the masked copy to the sender's mailbox. So it
 * settles like any other reply, with the undo it comes with, and says the one
 * true thing that is different about it. Until this existed the owner was told
 * "Sent." for a message that had not been.
 */
export const RELAY_FAILED_NOTE =
  "Your reply landed here, but the copy to their address did not go out. " +
  "They will see it when they open the knock.";

/**
 * The per-card interactive state, held by the hook so each card stays a pure
 * render. One superset for both surfaces: the recap's items simply never read
 * `expanded` or `blockOpen`.
 */
export type CardState = {
  expanded: boolean;
  composerOpen: boolean;
  replyDraft: string;
  dismissOpen: boolean;
  /** Inside the dismissal: "Other" was picked, so the one-line box is showing. */
  dismissOther: boolean;
  dismissNote: string;
  blockOpen: boolean;
  feedbackOpen: boolean;
  feedbackDone: "helpful" | "not_helpful" | null;
  pending: { verb: string; token: string } | null;
  note: string | null;
  busy: boolean;
};

export const CLOSED: CardState = {
  expanded: false,
  composerOpen: false,
  replyDraft: "",
  dismissOpen: false,
  dismissOther: false,
  dismissNote: "",
  blockOpen: false,
  feedbackOpen: false,
  feedbackDone: null,
  pending: null,
  note: null,
  busy: false,
};

/**
 * THE FOUR PANELS, AND ONLY ONE OF THEM IS EVER OPEN (owner, 2026-08-26).
 *
 * They all hang off one card and they all ask a different question about the
 * same knock: write to them, why not, are you sure, what was off. `patch` used
 * to be a plain shallow merge and every caller opened its own with a
 * single-key patch, so pressing two verbs left two boxes open under one card —
 * the "confusing double follow-ups" the owner reported.
 *
 * The exclusion is enforced HERE, in the one place every open goes through,
 * rather than by asking each call site to remember to shut the other three. A
 * call site that forgets is a bug that comes back; a reducer that cannot
 * express the state is not.
 */
const PANELS = ["composerOpen", "dismissOpen", "blockOpen", "feedbackOpen"] as const;
type Panel = (typeof PANELS)[number];

/** Which panel a card is showing, or null. The card and the desk both read it. */
export function openPanel(state: {
  composerOpen: boolean;
  dismissOpen: boolean;
  blockOpen: boolean;
  feedbackOpen: boolean;
}): Panel | null {
  return PANELS.find((key) => state[key]) ?? null;
}

/**
 * The recipient's three writes — act, undo, feedback — with the 30-second undo
 * window (FR-6.4), shared by the inbox drill-down and the Morning Nob so the
 * same decision behaves the same way on both surfaces.
 *
 * None of the writes refetches the list it acted on: an action stays reversible
 * for thirty seconds, and a refetch would take the card — and the Undo with
 * it — off the screen the moment it was promised. `onActed` is where a caller
 * says what OTHER surface goes stale (the inbox invalidates the briefing; the
 * briefing, being on screen, invalidates nothing).
 *
 * A write that fails for anything but an expired session surfaces on the card
 * as a note and leaves the row standing, so the decision can be retried rather
 * than silently swallowed.
 */
export function useInboxActions({
  onSessionExpired,
  onActed,
}: {
  onSessionExpired: () => void;
  onActed?: () => void;
}) {
  const [cards, setCards] = useState<Record<string, CardState>>({});

  const expired = useRef(onSessionExpired);
  expired.current = onSessionExpired;
  const acted = useRef(onActed);
  acted.current = onActed;

  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  useEffect(() => {
    const map = timers.current;
    return () => map.forEach((t) => clearTimeout(t));
  }, []);

  const state = useCallback((id: string): CardState => cards[id] ?? CLOSED, [cards]);
  /**
   * The one write into a card's state — and the one place the panels are kept
   * mutually exclusive (see PANELS above). A patch that opens one shuts the
   * other three, and the patch itself is applied last so an explicit key in it
   * always wins over the shutting.
   *
   * Closing "Other" travels with the dismissal that owns it: the box and the
   * line typed into it are meaningless once the question above them is gone.
   */
  const patch = useCallback((id: string, next: Partial<CardState>) => {
    setCards((prev) => {
      const before = prev[id] ?? CLOSED;
      const opening = PANELS.some((key) => next[key] === true);
      const shut: Partial<CardState> = {};
      if (opening) {
        for (const key of PANELS) if (next[key] !== true) shut[key] = false;
      }
      const merged = { ...before, ...shut, ...next };
      if (!merged.dismissOpen) {
        merged.dismissOther = false;
        merged.dismissNote = "";
      }
      return { ...prev, [id]: merged };
    });
  }, []);

  /**
   * The 30-second countdown that takes the undo away again — and, when it runs
   * out, tells the caller the decision is now final.
   *
   * That second half is what makes Block survivable. A block takes the
   * conversation out of every list the server answers, so invalidating the
   * inbox the instant the button is pressed would unmount the very card holding
   * the Undo we just promised. The list is therefore left stale for exactly as
   * long as the promise stands, and re-read the moment it lapses.
   */
  const arm = useCallback(
    (id: string) => {
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          patch(id, { pending: null });
          acted.current?.();
        }, UNDO_MS),
      );
    },
    [patch],
  );

  /** One landing for every action's outcome: the dry confirm and the undo it
   * comes with, then the timer that takes the undo away again. */
  const settle = useCallback(
    (id: string, verb: string, note: string, token: string) => {
      patch(id, {
        note,
        pending: { verb, token },
        composerOpen: false,
        dismissOpen: false,
        blockOpen: false,
        replyDraft: "",
        busy: false,
      });
      arm(id);
    },
    [patch, arm],
  );

  const act = useCallback(
    async (
      id: string,
      action: InboxAction,
      verb: string,
      note: string,
      extra?: { reason_chip?: InboxReasonChip | InboxDismissChip; body?: string },
    ) => {
      patch(id, { busy: true, note: null });
      const r = await submitInboxAction({
        knock_id: id,
        action,
        ...(extra?.reason_chip ? { reason_chip: extra.reason_chip } : {}),
        ...(extra?.body ? { note: extra.body } : {}),
      });
      if (!r.ok) {
        if (r.code === "UNAUTHENTICATED") {
          patch(id, { busy: false });
          expired.current();
          return;
        }
        // The action did not land: say so and leave the row, so the same
        // decision can be made again.
        patch(id, { busy: false, note: ACTION_FAILED_NOTE });
        return;
      }
      track({ name: "inbox_action", action });
      if (action === "book" && r.booking_url && typeof window !== "undefined") {
        window.open(r.booking_url, "_blank", "noopener,noreferrer");
      }
      /**
       * BLOCK IS THE ONE ACTION THAT DOES NOT REFRESH ANYTHING YET.
       *
       * Every other verb leaves the row where it is and only changes how it
       * reads, so telling the surfaces to re-read is free. A block deletes the
       * row from the lists — that is what the owner asked for — and doing it now
       * would take the card, and the Undo standing on it, off the screen in the
       * same breath as offering it. `arm` re-reads when the window shuts.
       */
      if (action !== "block") acted.current?.();
      // The relay is a nudge and cannot refuse a reply (`inbox/relay.ts`), so a
      // refused copy changes the WORDS on the card and nothing else about it.
      settle(id, verb, r.reply_sent === false ? RELAY_FAILED_NOTE : note, r.undo_token);
    },
    [patch, settle],
  );

  const undo = useCallback(
    async (id: string) => {
      const pending = state(id).pending;
      if (!pending) return;
      // The countdown pauses while the server is asked; it restarts if the
      // undo did not land, because the action is then still standing.
      const timer = timers.current.get(id);
      if (timer) clearTimeout(timer);
      timers.current.delete(id);
      patch(id, { busy: true });
      const r = await undoInboxAction({ undo_token: pending.token });
      if (!r.ok) {
        if (r.code === "UNAUTHENTICATED") {
          patch(id, { busy: false });
          expired.current();
          return;
        }
        patch(id, { busy: false, note: ACTION_FAILED_NOTE });
        arm(id);
        return;
      }
      // "Undone." only once the server has undone it — never before.
      patch(id, { busy: false, pending: null, note: "Undone." });
      // The undo cancelled the timer that would have said this, and something
      // real changed on the server: a block lifted, a Save put back, a line
      // taken out of somebody's transcript. The lists have to hear about it.
      acted.current?.();
    },
    [state, patch, arm],
  );

  const feedback = useCallback(
    async (id: string, helpful: boolean, reason?: InboxReasonChip) => {
      patch(id, { busy: true });
      const r = await submitInboxFeedback({
        knock_id: id,
        helpful,
        ...(reason ? { reason_chip: reason } : {}),
      });
      if (!r.ok) {
        if (r.code === "UNAUTHENTICATED") {
          patch(id, { busy: false });
          expired.current();
          return;
        }
        patch(id, { busy: false, note: ACTION_FAILED_NOTE });
        return;
      }
      track({ name: "inbox_feedback", helpful });
      acted.current?.();
      patch(id, {
        busy: false,
        feedbackOpen: false,
        feedbackDone: helpful ? "helpful" : "not_helpful",
        note: null,
      });
    },
    [patch],
  );

  return { cards, state, patch, act, undo, feedback };
}
